import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  LinkIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  Button,
  Input,
  Select,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui';
import { paymentLinkApi, templateApi, getErrorMessage } from '@/lib/api';
import type { CreatePaymentLinkInput, PaymentLink } from '@/types';

const paymentTypeOptions = [
  { value: 'RESERVATION_FEE', label: 'Reservation Fee' },
  { value: 'DOWN_PAYMENT', label: 'Down Payment' },
  { value: 'MONTHLY_AMORTIZATION', label: 'Monthly Amortization' },
  { value: 'SPOT_CASH', label: 'Spot Cash Payment' },
  { value: 'BALANCE_PAYMENT', label: 'Balance Payment' },
  { value: 'OTHER', label: 'Other' },
];

const locationOptions = [
  { value: 'Niseko', label: 'Hotel101 Niseko' },
  { value: 'Davao', label: 'Hotel101 Davao' },
  { value: 'Madrid', label: 'Hotel101 Madrid' },
  { value: 'Libis', label: 'Hotel101 Libis' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'PHP', label: 'PHP - Philippine Peso' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
  { value: 'NZD', label: 'NZD - New Zealand Dollar' },
  { value: 'KRW', label: 'KRW - South Korean Won' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
  { value: 'IDR', label: 'IDR - Indonesian Rupiah' },
  { value: 'VND', label: 'VND - Vietnamese Dong' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'AED', label: 'AED - UAE Dirham' },
];

const recurringFrequencyOptions = [
  { value: '', label: 'Not recurring' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' },
];

interface Template {
  id: string;
  name: string;
  description?: string;
  title: string;
  linkDescription?: string;
  paymentType: string;
  customPaymentType?: string;
  amount?: number;
  currency: string;
  allowPartialPayment: boolean;
  minimumAmount?: number;
  isRecurring: boolean;
  recurringFrequency?: string;
  recurringCount?: number;
  propertyLocation?: string;
  sendEmailOnCreate: boolean;
  sendReminderEmails: boolean;
  lineItems?: Array<{
    description: string;
    paymentType?: string;
    customPaymentType?: string;
    quantity?: number;
    unitPrice: number;
  }>;
  isActive: boolean;
}

interface LineItemFormData {
  description: string;
  paymentType: string;
  customPaymentType?: string;
  quantity: number;
  unitPrice: number;
  referenceNumber?: string;
  referenceDate?: string;
  isRecurring: boolean;
  recurringFrequency?: string;
  recurringCount?: number;
  recurringStartDate?: string;
  recurringEndDate?: string;
}

interface FormData {
  title: string;
  description?: string;
  currency: string;
  allowPartialPayment: boolean;
  minimumAmount?: number;
  expiresAt?: string;
  customerEmail?: string;
  customerName?: string;
  customerPhone?: string;
  propertyLocation?: string;
  unitNumber?: string;
  contractNumber?: string;
  sendEmailOnCreate: boolean;
  sendReminderEmails: boolean;
  lineItems: LineItemFormData[];
}

export function CreatePaymentLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createdLink, setCreatedLink] = useState<PaymentLink | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    searchParams.get('template') || ''
  );
  const [expandedItems, setExpandedItems] = useState<number[]>([0]);

  // Fetch templates for the dropdown
  const { data: templatesData } = useQuery({
    queryKey: ['templates', { isActive: true }],
    queryFn: () => templateApi.list({ isActive: true, limit: 100 }),
  });

  const templates: Template[] = templatesData?.data?.data || [];

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      currency: 'PHP',
      allowPartialPayment: false,
      sendEmailOnCreate: true,
      sendReminderEmails: true,
      lineItems: [{
        description: '',
        paymentType: 'RESERVATION_FEE',
        quantity: 1,
        unitPrice: 0,
        isRecurring: false,
      }],
    },
  });

  // Apply template when selected
  const applyTemplate = (template: Template) => {
    const templateLineItems = template.lineItems?.map(item => ({
      description: item.description,
      paymentType: item.paymentType || template.paymentType,
      customPaymentType: item.customPaymentType || template.customPaymentType || '',
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice,
      isRecurring: template.isRecurring,
      recurringFrequency: template.recurringFrequency || '',
      recurringCount: template.recurringCount || undefined,
    })) || [{
      description: '',
      paymentType: template.paymentType,
      customPaymentType: template.customPaymentType || '',
      quantity: 1,
      unitPrice: template.amount || 0,
      isRecurring: template.isRecurring,
      recurringFrequency: template.recurringFrequency || '',
      recurringCount: template.recurringCount || undefined,
    }];

    reset({
      title: template.title,
      description: template.linkDescription || '',
      currency: template.currency,
      allowPartialPayment: template.allowPartialPayment,
      minimumAmount: template.minimumAmount || undefined,
      propertyLocation: template.propertyLocation || '',
      sendEmailOnCreate: template.sendEmailOnCreate,
      sendReminderEmails: template.sendReminderEmails,
      lineItems: templateLineItems,
    });
    setExpandedItems([0]);
    toast.success(`Template "${template.name}" applied`);
  };

  // Handle template selection change
  useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        applyTemplate(template);
      }
    }
  }, [selectedTemplateId, templates.length]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const allowPartialPayment = watch('allowPartialPayment');
  const lineItems = watch('lineItems');
  const currency = watch('currency');

  // Calculate total from line items
  const calculateTotal = () => {
    if (!lineItems || lineItems.length === 0) return 0;
    return lineItems.reduce((sum, item) => {
      const quantity = item.quantity || 1;
      const unitPrice = item.unitPrice || 0;
      return sum + quantity * unitPrice;
    }, 0);
  };

  const toggleExpand = (index: number) => {
    setExpandedItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const createMutation = useMutation({
    mutationFn: (data: CreatePaymentLinkInput) => paymentLinkApi.create(data),
    onSuccess: (response) => {
      toast.success('Payment link created successfully');
      setCreatedLink(response.data.data);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const onSubmit = (data: FormData) => {
    // Check if any line item has recurring enabled
    const hasAnyRecurringItem = data.lineItems.some(item => item.isRecurring);

    // Get the first item's payment type as the main payment type
    const mainPaymentType = data.lineItems[0]?.paymentType || 'OTHER';
    const mainCustomPaymentType = data.lineItems[0]?.customPaymentType;

    // Prepare the payload with per-item recurring data
    const payload: CreatePaymentLinkInput = {
      title: data.title,
      description: data.description,
      paymentType: mainPaymentType as any,
      customPaymentType: mainPaymentType === 'OTHER' ? mainCustomPaymentType : undefined,
      currency: data.currency,
      allowPartialPayment: data.allowPartialPayment,
      minimumAmount: data.minimumAmount,
      expiresAt: data.expiresAt,
      customerEmail: data.customerEmail,
      customerName: data.customerName,
      customerPhone: data.customerPhone,
      propertyLocation: data.propertyLocation,
      unitNumber: data.unitNumber,
      contractNumber: data.contractNumber,
      sendEmailOnCreate: data.sendEmailOnCreate,
      sendReminderEmails: data.sendReminderEmails,
      // Include per-item recurring settings in line items
      lineItems: data.lineItems.map(item => ({
        description: item.description,
        paymentType: item.paymentType as any,
        customPaymentType: item.paymentType === 'OTHER' ? item.customPaymentType : undefined,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice,
        referenceNumber: item.referenceNumber,
        referenceDate: item.referenceDate,
        // Per-item recurring settings
        isRecurring: item.isRecurring || false,
        recurringFrequency: item.isRecurring ? item.recurringFrequency : undefined,
        recurringStartDate: item.isRecurring ? (item.recurringStartDate || item.referenceDate) : undefined,
        recurringEndDate: item.isRecurring ? item.recurringEndDate : undefined,
        recurringCount: item.isRecurring ? item.recurringCount : undefined,
      })),
      // Keep isRecurring at top level for backwards compatibility
      isRecurring: hasAnyRecurringItem,
    };

    createMutation.mutate(payload);
  };

  const addLineItem = () => {
    const newIndex = fields.length;
    append({
      description: '',
      paymentType: 'RESERVATION_FEE',
      quantity: 1,
      unitPrice: 0,
      isRecurring: false,
    });
    setExpandedItems(prev => [...prev, newIndex]);
  };

  const copyLink = () => {
    if (createdLink) {
      const metadata = createdLink.metadata as { adyenLinkUrl?: string } | null;
      const url = metadata?.adyenLinkUrl || `${window.location.origin}/pay/${createdLink.linkCode}`;
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const getPaymentTypeLabel = (type: string) => {
    const option = paymentTypeOptions.find(o => o.value === type);
    return option?.label || type;
  };

  // Success state
  if (createdLink) {
    const metadata = createdLink.metadata as { adyenLinkUrl?: string } | null;
    const paymentUrl = metadata?.adyenLinkUrl || `${window.location.origin}/pay/${createdLink.linkCode}`;

    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardBody className="text-center py-12">
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Link Created!
            </h2>
            <p className="text-gray-500 mb-6">
              Your payment link has been created and is ready to be shared with your customer.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-2">Payment Link</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={paymentUrl}
                  readOnly
                  className="flex-1 text-sm bg-white border border-gray-200 rounded px-3 py-2"
                />
                <Button onClick={copyLink}>Copy</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-left mb-8">
              <div>
                <p className="text-xs text-gray-500">Amount</p>
                <p className="font-medium">
                  {createdLink.currency} {createdLink.amount.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Customer</p>
                <p className="font-medium">{createdLink.customerName || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Property</p>
                <p className="font-medium">{createdLink.propertyLocation || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Unit</p>
                <p className="font-medium">{createdLink.unitNumber || '-'}</p>
              </div>
              {createdLink.isRecurring && (
                <>
                  <div>
                    <p className="text-xs text-gray-500">Recurring</p>
                    <p className="font-medium">{createdLink.recurringFrequency}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Schedules</p>
                    <p className="font-medium">
                      {createdLink.recurringSchedules?.length || 0} payments
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/admin/payment-links')}>
                View All Links
              </Button>
              <Button onClick={() => setCreatedLink(null)}>
                Create Another
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/payment-links')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        Back to Payment Links
      </button>

      {/* Template Selector */}
      {templates.length > 0 && (
        <Card className="mb-6">
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-700">
                <DocumentDuplicateIcon className="w-5 h-5" />
                <span className="font-medium">Start from template:</span>
              </div>
              <div className="flex-1">
                <select
                  value={selectedTemplateId}
                  onChange={(e) => {
                    const templateId = e.target.value;
                    setSelectedTemplateId(templateId);
                    if (templateId) {
                      const template = templates.find(t => t.id === templateId);
                      if (template) {
                        applyTemplate(template);
                      }
                    }
                  }}
                  className="w-full rounded-lg border-gray-300 text-sm focus:border-primary focus:ring-primary"
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                      {template.amount ? ` (${template.currency} ${template.amount.toLocaleString()})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {selectedTemplateId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedTemplateId('');
                    reset({
                      currency: 'PHP',
                      allowPartialPayment: false,
                      sendEmailOnCreate: true,
                      sendReminderEmails: true,
                      lineItems: [{
                        description: '',
                        paymentType: 'RESERVATION_FEE',
                        quantity: 1,
                        unitPrice: 0,
                        isRecurring: false,
                      }],
                    });
                    setExpandedItems([0]);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>
                Basic information for this payment link
              </CardDescription>
            </CardHeader>
            <CardBody className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Title"
                  required
                  placeholder="e.g., Reservation Fee - Unit DAV-1501"
                  error={errors.title?.message}
                  {...register('title', { required: 'Title is required' })}
                />
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Description"
                  placeholder="Additional details about this payment..."
                  {...register('description')}
                />
              </div>

              <Select
                label="Currency"
                options={currencyOptions}
                {...register('currency')}
              />

              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    {...register('allowPartialPayment')}
                  />
                  <span className="text-sm text-gray-700">Allow partial payments</span>
                </label>
              </div>

              {allowPartialPayment && (
                <Input
                  label="Minimum Payment Amount"
                  type="number"
                  min={1}
                  step="0.01"
                  placeholder="0.00"
                  hint="Minimum amount the customer can pay per transaction"
                  {...register('minimumAmount', { valueAsNumber: true })}
                />
              )}
            </CardBody>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Items</CardTitle>
              <CardDescription>
                Add items to this payment link. Each item can have its own payment type and recurring settings.
              </CardDescription>
            </CardHeader>
            <CardBody className="space-y-4">
              {fields.map((field, index) => {
                const isExpanded = expandedItems.includes(index);
                const item = lineItems[index];
                const itemTotal = (item?.quantity || 1) * (item?.unitPrice || 0);

                return (
                  <div
                    key={field.id}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Item Header - Always visible */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleExpand(index)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">
                          Item {index + 1}
                        </span>
                        {item?.description && (
                          <span className="text-sm text-gray-500">
                            - {item.description}
                          </span>
                        )}
                        {item?.paymentType && (
                          <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded">
                            {item.paymentType === 'OTHER' && item.customPaymentType
                              ? item.customPaymentType
                              : getPaymentTypeLabel(item.paymentType)}
                          </span>
                        )}
                        {item?.isRecurring && item?.recurringFrequency && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            {item.recurringFrequency}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-900">
                          {currency} {itemTotal.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(index);
                              setExpandedItems(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                        {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Item Details - Expandable */}
                    {isExpanded && (
                      <div className="p-4 space-y-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            label="Description"
                            placeholder="e.g., March 2024 Amortization"
                            error={errors.lineItems?.[index]?.description?.message}
                            {...register(`lineItems.${index}.description`, {
                              required: 'Description is required',
                            })}
                          />
                          <div>
                            <Select
                              label="Payment Type"
                              options={paymentTypeOptions}
                              {...register(`lineItems.${index}.paymentType`, {
                                onChange: () => setValue(`lineItems.${index}.customPaymentType`, ''),
                              })}
                            />
                            {item?.paymentType === 'OTHER' && (
                              <Input
                                placeholder="Enter custom payment type"
                                className="mt-2"
                                {...register(`lineItems.${index}.customPaymentType`)}
                              />
                            )}
                          </div>
                          <Input
                            label="Quantity"
                            type="number"
                            min={1}
                            {...register(`lineItems.${index}.quantity`, {
                              valueAsNumber: true,
                              min: 1,
                            })}
                          />
                          <Input
                            label="Unit Price"
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="0.00"
                            error={errors.lineItems?.[index]?.unitPrice?.message}
                            {...register(`lineItems.${index}.unitPrice`, {
                              required: 'Unit price is required',
                              valueAsNumber: true,
                              min: { value: 0, message: 'Price must be positive' },
                            })}
                          />
                          <Input
                            label="Reference"
                            placeholder="e.g., Mar 2024"
                            {...register(`lineItems.${index}.referenceNumber`)}
                          />
                          <Input
                            label="Due Date"
                            type="date"
                            {...register(`lineItems.${index}.referenceDate`)}
                          />
                        </div>

                        {/* Recurring options per item */}
                        <div className="pt-4 border-t border-gray-100">
                          <label className="flex items-center gap-2 mb-4">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              {...register(`lineItems.${index}.isRecurring`)}
                            />
                            <span className="text-sm font-medium text-gray-700">Make this a recurring payment</span>
                          </label>

                          {item?.isRecurring && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                              <Select
                                label="Frequency"
                                options={recurringFrequencyOptions.filter(o => o.value !== '')}
                                {...register(`lineItems.${index}.recurringFrequency`)}
                              />
                              <Input
                                label="Number of Payments"
                                type="number"
                                min={1}
                                placeholder="e.g., 12"
                                {...register(`lineItems.${index}.recurringCount`, { valueAsNumber: true })}
                              />
                              <Input
                                label="Start Date"
                                type="date"
                                {...register(`lineItems.${index}.recurringStartDate`)}
                              />
                              <Input
                                label="End Date"
                                type="date"
                                hint="Optional"
                                {...register(`lineItems.${index}.recurringEndDate`)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                onClick={addLineItem}
                leftIcon={<PlusIcon className="w-4 h-4" />}
              >
                Add Item
              </Button>

              {lineItems.length > 0 && (
                <div className="flex justify-end pt-4 border-t">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {currency} {calculateTotal().toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
              <CardDescription>
                Optional: Pre-fill customer details for the payment
              </CardDescription>
            </CardHeader>
            <CardBody className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Customer Name"
                placeholder="Juan Dela Cruz"
                {...register('customerName')}
              />

              <Input
                label="Customer Email"
                type="email"
                placeholder="customer@email.com"
                {...register('customerEmail')}
              />

              <Input
                label="Customer Phone"
                type="tel"
                placeholder="+639123456789"
                {...register('customerPhone')}
              />
            </CardBody>
          </Card>

          {/* Property Information */}
          <Card>
            <CardHeader>
              <CardTitle>Property Information</CardTitle>
              <CardDescription>
                Link this payment to a specific property
              </CardDescription>
            </CardHeader>
            <CardBody className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Select
                label="Property Location"
                options={[{ value: '', label: 'Select location' }, ...locationOptions]}
                {...register('propertyLocation')}
              />

              <Input
                label="Unit Number"
                placeholder="e.g., DAV-1501"
                {...register('unitNumber')}
              />

              <Input
                label="Contract Number"
                placeholder="e.g., CTR-2024-001"
                {...register('contractNumber')}
              />
            </CardBody>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <Input
                label="Expiry Date"
                type="datetime-local"
                hint="Leave empty for 30-day default expiry"
                {...register('expiresAt')}
              />

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...register('sendEmailOnCreate')}
                />
                <span className="text-sm text-gray-700">
                  Send email notification to customer when link is created
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...register('sendReminderEmails')}
                />
                <span className="text-sm text-gray-700">
                  Send reminder emails before link expires
                </span>
              </label>
            </CardBody>
            <CardFooter className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/payment-links')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={createMutation.isPending}
                leftIcon={<LinkIcon className="w-4 h-4" />}
              >
                Create Payment Link
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
